import { useCallback, useEffect } from 'react';
import { useFetcher } from '@remix-run/react';
import type { SerializeFrom } from '@remix-run/node';
import { atom, useAtom } from 'jotai';
import {
    NOTIFICATIONS_API,
    type loader,
} from '~/routes/remix-api.user.notifications';
import { useAppData } from '~/modules/root';

const notificationsAtom = atom<SerializeFrom<typeof loader>>({
    designBoard: { count: 0 },
    website: { count: 0 },
    cart: { count: 0 },
});

/**
 * Hook to get the notifications for the current user. The data is initially
 * fetched from the root _app.tsx loader and then is polled from the
 * notifications API endpoint. Can be refetched on demand as well.
 *
 * This hook should be called only once with the `global` argument set to `true`
 * in the root component to make sure it is updated with polling. Subsequent
 * usage should have the `global` argument set to `false`.
 */
export function useNotifications({
    global = false,
}: {
    /**
     * Include the `useEffect` calls to update the notifications with polling.
     * Only one instance of this hook should be called with the `global=true`
     * in the root component to prevent duplicate polling.
     */
    global?: boolean;
} = {}) {
    const [notifications, setNotifications] = useAtom(notificationsAtom);
    const fetcher = useFetcher<typeof loader>({ key: 'notifications' });
    const { notifications: initialNotifications } = useAppData();

    useEffect(() => {
        if (!global) {
            return;
        }
        // Update notifications when the _app.tsx loader is revalidated.
        setNotifications(initialNotifications);
    }, [global, initialNotifications, setNotifications]);

    useEffect(() => {
        if (!global) {
            return;
        }
        if (fetcher.data) {
            setNotifications(fetcher.data);
        }
    }, [global, fetcher.data, setNotifications]);

    useEffect(() => {
        if (!global) {
            return;
        }
        const pollingInterval = 300_000; // 5 minutes
        const interval = setInterval(() => {
            fetcher.load(NOTIFICATIONS_API);
        }, pollingInterval);
        return () => clearInterval(interval);
    }, [global, fetcher]);

    return {
        notifications,
        refetch: useCallback(() => {
            fetcher.load(NOTIFICATIONS_API);
        }, [fetcher]),
    };
}
